/* ==========================================================================
   Vela - Announcement Bar
   Rotation (fade / slide / marquee / show-all), countdowns, swipe, dismiss.
   Dependency free. Safe to load more than once.
   ========================================================================== */
(function () {
  'use strict';

  if (window.velaAnnouncementBar) return;

  var SELECTOR = '[data-vela-announcement]';
  var MOBILE_MQ = '(max-width: 767px)';
  var instances = [];

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* Storage helpers - localStorage can throw in private mode / blocked cookies */
  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) { /* ignore */ }
  }

  /* Cheap deterministic hash so editing the messages re-shows a dismissed bar */
  function fingerprint(str) {
    var hash = 5381;
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
  }

  function pad(value) {
    return value < 10 ? '0' + value : '' + value;
  }

  /* Merchants type "2026-12-31 23:59"; Safari needs a little help with that */
  function parseDate(input) {
    if (!input) return NaN;
    var value = String(input).trim();

    var parsed = Date.parse(value);
    if (!isNaN(parsed)) return parsed;

    parsed = Date.parse(value.replace(' ', 'T'));
    if (!isNaN(parsed)) return parsed;

    var parts = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (parts) {
      return new Date(
        parseInt(parts[1], 10),
        parseInt(parts[2], 10) - 1,
        parseInt(parts[3], 10),
        parseInt(parts[4] || 0, 10),
        parseInt(parts[5] || 0, 10),
        parseInt(parts[6] || 0, 10)
      ).getTime();
    }

    return NaN;
  }

  /* ------------------------------------------------------------------------
     Instance
     ------------------------------------------------------------------------ */
  function AnnouncementBar(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-va-viewport]');
    this.slidesWrap = root.querySelector('[data-va-slides]');
    this.slides = toArray(root.querySelectorAll('[data-va-slide]'));
    this.marquee = root.querySelector('[data-va-marquee]');
    this.track = root.querySelector('[data-va-track]');
    this.group = root.querySelector('[data-va-group]');
    this.closeBtn = root.querySelector('[data-va-close]');
    this.prevBtn = root.querySelector('[data-va-prev]');
    this.nextBtn = root.querySelector('[data-va-next]');
    this.liveRegion = root.querySelector('[data-va-live]');

    this.configuredMode = root.getAttribute('data-animation') || 'fade';
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.speed = parseInt(root.getAttribute('data-speed'), 10) || 5000;
    this.marqueeSpeed = parseInt(root.getAttribute('data-marquee-speed'), 10) || 40;
    this.pauseOnHover = root.getAttribute('data-pause-hover') !== 'false';
    this.dismissDays = parseInt(root.getAttribute('data-dismiss-days'), 10) || 7;
    this.sectionId = root.getAttribute('data-section-id') || 'announcement';

    this.index = 0;
    this.timer = null;
    this.leaveTimer = null;
    this.countdownTimer = null;
    this.paused = false;
    this.mode = null;
    this.mql = window.matchMedia ? window.matchMedia(MOBILE_MQ) : null;

    this.init();
  }

  AnnouncementBar.prototype.init = function () {
    if (this.root.getAttribute('data-va-initialized') === 'true') return;
    this.root.setAttribute('data-va-initialized', 'true');

    document.body.classList.add('velaHasAnnouncement');
    if (this.root.getAttribute('data-sticky') === 'true') {
      document.body.classList.add('velaAnnouncementSticky');
    }

    if (this.isDismissed()) {
      this.root.classList.add('velaAnnouncementBar--dismissed');
      this.root.setAttribute('hidden', 'hidden');
      this.publishHeight();
      return;
    }

    this.bindEvents();
    this.startCountdowns();
    this.applyMode();
    this.publishHeight();
  };

  /* --- Dismiss ----------------------------------------------------------- */
  AnnouncementBar.prototype.storageKey = function () {
    var content = this.slides
      .map(function (slide) {
        return slide.textContent.replace(/\s+/g, ' ').trim();
      })
      .join('|');
    return 'vela:announcement:' + this.sectionId + ':' + fingerprint(content);
  };

  AnnouncementBar.prototype.isDismissed = function () {
    if (isDesignMode()) return false;
    if (!this.closeBtn) return false;
    var stamp = parseInt(storageGet(this.storageKey()), 10);
    if (!stamp) return false;
    return Date.now() - stamp < this.dismissDays * 86400000;
  };

  AnnouncementBar.prototype.dismiss = function () {
    var self = this;
    storageSet(this.storageKey(), String(Date.now()));

    this.stop();
    this.root.style.height = this.root.offsetHeight + 'px';
    /* force reflow so the height transition has a start value */
    void this.root.offsetHeight;
    this.root.classList.add('velaAnnouncementBar--closing');
    this.publishHeight(0);

    window.setTimeout(function () {
      self.root.classList.add('velaAnnouncementBar--dismissed');
      self.root.setAttribute('hidden', 'hidden');
      self.root.style.height = '';
      self.root.classList.remove('velaAnnouncementBar--closing');
    }, 380);
  };

  /* --- Height ------------------------------------------------------------
     Two theme rules need to know how tall the bar is: the floating header
     (position: fixed, top: 0) and the home page header, which the theme
     absolutely positions at top: 0 on screens 992px and wider.
     ------------------------------------------------------------------------ */
  AnnouncementBar.prototype.publishHeight = function (forced) {
    var height = typeof forced === 'number' ? forced : this.root.offsetHeight;
    document.documentElement.style.setProperty('--vela-announcement-height', height + 'px');
  };

  /* --- Mode -------------------------------------------------------------- */
  AnnouncementBar.prototype.effectiveMode = function () {
    var onMobile = this.mql ? this.mql.matches : window.innerWidth <= 767;
    /* "Show all" only fits side by side on larger screens - rotate on phones */
    if (this.configuredMode === 'all') return onMobile ? 'fade' : 'all';
    return this.configuredMode;
  };

  AnnouncementBar.prototype.applyMode = function () {
    var mode = this.effectiveMode();
    if (mode === this.mode) return;

    this.stop();
    this.mode = mode;
    this.root.setAttribute('data-mode', mode);

    if (mode === 'marquee') {
      this.setupMarquee();
      return;
    }

    this.root.removeAttribute('data-marquee-ready');

    if (mode === 'all') {
      this.slides.forEach(function (slide) {
        slide.removeAttribute('aria-hidden');
        slide.classList.remove('is-active', 'is-leaving');
      });
      return;
    }

    this.setupRotation();
  };

  /* --- Rotation ---------------------------------------------------------- */
  AnnouncementBar.prototype.visibleSlides = function () {
    var onMobile = this.mql ? this.mql.matches : window.innerWidth <= 767;
    return this.slides.filter(function (slide) {
      if (slide.hasAttribute('hidden')) return false;
      if (onMobile && slide.getAttribute('data-mobile') === 'false') return false;
      return true;
    });
  };

  AnnouncementBar.prototype.setupRotation = function () {
    var visible = this.visibleSlides();

    this.slides.forEach(function (slide) {
      slide.classList.remove('is-active', 'is-leaving');
      slide.setAttribute('aria-hidden', 'true');
    });

    if (!visible.length) return;

    this.index = 0;
    visible[0].classList.add('is-active');
    visible[0].removeAttribute('aria-hidden');

    var single = visible.length < 2;
    this.root.setAttribute('data-single', single ? 'true' : 'false');
    if (single) return;

    this.start();
  };

  AnnouncementBar.prototype.goTo = function (nextIndex, direction, announce) {
    var visible = this.visibleSlides();
    if (visible.length < 2) return;

    var total = visible.length;
    var target = ((nextIndex % total) + total) % total;
    if (target === this.index) return;

    var current = visible[this.index];
    var next = visible[target];
    if (!next) return;

    this.root.setAttribute('data-dir', direction === 'prev' ? 'prev' : 'next');

    if (current) {
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
    }

    next.classList.remove('is-leaving');
    /* reflow so the entering slide animates from its offset position */
    void next.offsetWidth;
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');

    this.index = target;

    window.clearTimeout(this.leaveTimer);
    this.leaveTimer = window.setTimeout(function () {
      if (current) current.classList.remove('is-leaving');
    }, 450);

    /* Only announce visitor-driven changes - autoplay would chatter endlessly */
    if (announce && this.liveRegion) {
      this.liveRegion.textContent = next.textContent.replace(/\s+/g, ' ').trim();
    }
  };

  AnnouncementBar.prototype.next = function (announce) {
    this.goTo(this.index + 1, 'next', announce);
  };

  AnnouncementBar.prototype.prev = function (announce) {
    this.goTo(this.index - 1, 'prev', announce);
  };

  AnnouncementBar.prototype.start = function () {
    var self = this;
    this.stopTimer();
    if (!this.autoplay || this.paused) return;
    if (this.visibleSlides().length < 2) return;
    this.timer = window.setInterval(function () {
      if (document.hidden) return;
      self.next();
    }, this.speed);
  };

  AnnouncementBar.prototype.stopTimer = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  AnnouncementBar.prototype.stop = function () {
    this.stopTimer();
    window.clearTimeout(this.leaveTimer);
  };

  AnnouncementBar.prototype.setPaused = function (state) {
    this.paused = state;
    this.root.setAttribute('data-paused', state ? 'true' : 'false');
    if (state) {
      this.stopTimer();
    } else {
      this.start();
    }
  };

  /* --- Marquee ----------------------------------------------------------- */
  AnnouncementBar.prototype.setupMarquee = function () {
    if (!this.track || !this.group) return;

    var self = this;
    this.root.removeAttribute('data-marquee-ready');
    this.root.setAttribute('data-single', 'false');

    toArray(this.track.querySelectorAll('[data-va-clone]')).forEach(function (clone) {
      clone.parentNode.removeChild(clone);
    });

    if (prefersReducedMotion()) return;

    /* Wait a frame so fonts/layout have settled before measuring */
    window.requestAnimationFrame(function () {
      var groupWidth = self.group.offsetWidth;
      var frameWidth = self.marquee ? self.marquee.offsetWidth : self.root.offsetWidth;
      if (!groupWidth || !frameWidth) return;

      var copies = Math.ceil(frameWidth / groupWidth) + 1;
      var fragment = document.createDocumentFragment();

      for (var i = 0; i < copies; i++) {
        var clone = self.group.cloneNode(true);
        clone.setAttribute('data-va-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        toArray(clone.querySelectorAll('a, button')).forEach(function (node) {
          node.setAttribute('tabindex', '-1');
        });
        fragment.appendChild(clone);
      }
      self.track.appendChild(fragment);

      /* One group width of travel loops seamlessly - every group is identical */
      var duration = groupWidth / Math.max(self.marqueeSpeed, 5);
      self.root.style.setProperty('--vaBar-mq-shift', '-' + groupWidth + 'px');
      self.root.style.setProperty('--vaBar-mq-dur', duration.toFixed(2) + 's');
      self.root.setAttribute('data-marquee-ready', 'true');
    });
  };

  /* --- Countdowns -------------------------------------------------------- */
  AnnouncementBar.prototype.startCountdowns = function () {
    var self = this;
    this.countdowns = toArray(this.root.querySelectorAll('[data-va-countdown]')).filter(function (node) {
      var end = parseDate(node.getAttribute('data-end'));
      if (isNaN(end)) {
        node.setAttribute('hidden', 'hidden');
        return false;
      }
      node.velaEnd = end;
      return true;
    });

    if (!this.countdowns.length) return;

    this.tickCountdowns();
    this.countdownTimer = window.setInterval(function () {
      self.tickCountdowns();
    }, 1000);
  };

  AnnouncementBar.prototype.tickCountdowns = function () {
    var self = this;
    var stillRunning = 0;

    this.countdowns.forEach(function (node) {
      var remaining = node.velaEnd - Date.now();

      if (remaining <= 0) {
        node.setAttribute('hidden', 'hidden');
        var slide = node.closest ? node.closest('[data-va-slide]') : null;
        if (slide && slide.getAttribute('data-hide-expired') === 'true') {
          slide.setAttribute('hidden', 'hidden');
          self.refreshAfterExpiry = true;
        }
        return;
      }

      stillRunning++;
      var totalSeconds = Math.floor(remaining / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      var units = node.querySelectorAll('[data-va-cd]');
      toArray(units).forEach(function (unit) {
        var key = unit.getAttribute('data-va-cd');
        var value = unit.querySelector('[data-va-cd-value]');
        if (!value) return;
        if (key === 'days') value.textContent = pad(days);
        if (key === 'hours') value.textContent = pad(hours);
        if (key === 'minutes') value.textContent = pad(minutes);
        if (key === 'seconds') value.textContent = pad(seconds);
      });

      /* Drop the day chip once there are no days left */
      var dayUnit = node.querySelector('[data-va-cd="days"]');
      if (dayUnit) {
        if (days > 0) {
          dayUnit.removeAttribute('hidden');
        } else {
          dayUnit.setAttribute('hidden', 'hidden');
        }
      }
    });

    if (this.refreshAfterExpiry) {
      this.refreshAfterExpiry = false;
      if (this.mode !== 'marquee' && this.mode !== 'all') this.setupRotation();
      this.publishHeight();
    }

    if (!stillRunning && this.countdownTimer) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  };

  /* --- Events ------------------------------------------------------------ */
  AnnouncementBar.prototype.bindEvents = function () {
    var self = this;

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.dismiss();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.next(true);
        self.start();
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (event) {
        event.preventDefault();
        self.prev(true);
        self.start();
      });
    }

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', function () {
        self.setPaused(true);
      });
      this.root.addEventListener('mouseleave', function () {
        self.setPaused(false);
      });
    }

    /* Always pause while a control inside the bar has keyboard focus */
    this.root.addEventListener('focusin', function () {
      self.setPaused(true);
    });
    this.root.addEventListener('focusout', function (event) {
      if (!self.root.contains(event.relatedTarget)) self.setPaused(false);
    });

    this.onVisibility = function () {
      if (document.hidden) {
        self.stopTimer();
      } else if (!self.paused) {
        self.start();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.bindSwipe();
    this.bindResize();
  };

  AnnouncementBar.prototype.bindSwipe = function () {
    if (!this.viewport) return;
    var self = this;
    var startX = 0;
    var startY = 0;
    var tracking = false;

    this.viewport.addEventListener('touchstart', function (event) {
      if (self.mode === 'marquee' || self.mode === 'all') return;
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      tracking = true;
    }, { passive: true });

    this.viewport.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;
      var touch = event.changedTouches[0];
      var deltaX = touch.clientX - startX;
      var deltaY = touch.clientY - startY;
      /* Horizontal intent only, so page scrolling still wins */
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
      if (deltaX < 0) {
        self.next(true);
      } else {
        self.prev(true);
      }
      self.start();
    }, { passive: true });
  };

  AnnouncementBar.prototype.bindResize = function () {
    var self = this;
    var raf = null;

    this.onResize = function () {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(function () {
        self.applyMode();
        if (self.mode === 'marquee') self.setupMarquee();
        self.publishHeight();
      });
    };

    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  };

  AnnouncementBar.prototype.destroy = function () {
    this.stop();
    if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
    }
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    this.root.removeAttribute('data-va-initialized');
  };

  /* ------------------------------------------------------------------------
     Bootstrap
     ------------------------------------------------------------------------ */
  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = toArray(context.querySelectorAll(SELECTOR));
    if (context.matches && context.matches(SELECTOR)) roots.push(context);

    roots.forEach(function (root) {
      if (root.getAttribute('data-va-initialized') === 'true') return;
      instances.push(new AnnouncementBar(root));
    });
  }

  function destroyIn(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (scope && scope.contains && scope.contains(instances[i].root)) {
        instances[i].destroy();
        instances.splice(i, 1);
      }
    }
  }

  window.velaAnnouncementBar = {
    init: initAll,
    instances: instances
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Re-measure once webfonts land - text width drives the marquee loop */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      instances.forEach(function (instance) {
        if (instance.mode === 'marquee') instance.setupMarquee();
        instance.publishHeight();
      });
    });
  }

  /* Theme editor */
  document.addEventListener('shopify:section:load', function (event) {
    destroyIn(event.target);
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var root = event.target.querySelector(SELECTOR);
    if (root) {
      root.classList.remove('velaAnnouncementBar--dismissed');
      root.removeAttribute('hidden');
    }
  });
})();
